/* eslint-disable no-nested-ternary */
import React from 'react';
import { DateTime } from 'luxon';
import { Icon } from '@patternfly/react-core';
import {
  ExclamationTriangleIcon,
  InfoCircleIcon,
} from '@patternfly/react-icons';
import { observer } from 'mobx-react';
import { withErrorBoundary } from 'react-error-boundary';
import { handleError } from 'services/ErrorService';
import ComponentError from 'components/Error/ComponentError';
import { useViewportSize } from '@ole-ui/ole-ui-components';
import './styles.scss';
import { extractFromSlug } from 'helpers/utils';
import useMobxStores from 'hooks/useMobxStores';
import { useTranslation } from 'react-i18next';
import { UserEntitlement } from 'types/user';
import SubscriptionsTable from '../SubscriptionsTable';
import ExpandableSubscriptionWidget from './ExpandableSubscriptionWidget';

const SubscriptionWidget = () => {
  const isMobile = useViewportSize().isLowerThanMd;

  const { t } = useTranslation();
  const { userStore, routerStore } = useMobxStores() as any;
  const { enforceLabLimit, sortedEntitlements } = userStore;

  if (isMobile) {
    return <SubscriptionsTable />;
  }

  const { route } = routerStore;

  const isInALCPage =
    userStore.isLoggedIn &&
    userStore.isAlacarteSubscriberForCourse(route?.params?.course) &&
    route.name.includes('courses');

  const { code: currentCourse } = extractFromSlug(route?.params?.course);

  const exclamationIcon = (
    <React.Fragment>
      <Icon status="warning" size="lg" className="exclamation-triangle-icon">
        <ExclamationTriangleIcon />
      </Icon>
    </React.Fragment>
  );

  const infoCircleIcon = (
    <React.Fragment>
      <Icon status="info" size="lg" className="exclamation-triangle-icon">
        <InfoCircleIcon />
      </Icon>
    </React.Fragment>
  );

  const getLabUsageRemaining = (
    used: number = 0,
    allowed: number = 0,
  ): React.ReactElement => {
    const hours = Math.max(allowed - used, 0);

    const labHoursRemainingTxt = <>{t('Lab hours remaining')}:&nbsp;</>;

    if (hours > 20 || !enforceLabLimit) {
      return (
        <>
          {t('Lab hours used')} &nbsp;
          <strong>
            {Math.round(used)}/{allowed}
          </strong>
        </>
      );
    }

    if (hours > 10) {
      return (
        <>
          {infoCircleIcon} {labHoursRemainingTxt}
          <span className="usage-info-alert">
            <strong>
              {t('less than')} {hours}
            </strong>
          </span>
        </>
      );
    }

    if (hours > 0) {
      return (
        <>
          {exclamationIcon} {labHoursRemainingTxt}
          <span className="usage-alert">
            <strong>
              {t('less than')} {hours}
            </strong>
          </span>
        </>
      );
    }

    return (
      <div className="usage-alert-bg">
        <span className="usage-alert">
          <strong>{t('All lab hours have been used')}</strong>
        </span>
      </div>
    );
  };

  const getRemainingEntitlementTime = (endTime: string) =>
    DateTime.fromISO(endTime).diff(DateTime.now(), 'days').days;

  return (
    <div className="subscription-widget">
      {sortedEntitlements
        .filter((entitlement: UserEntitlement) => {
          if (!isInALCPage) return true;

          const { code: entitlementCourse } = extractFromSlug(
            entitlement.entitlement_info.offering_slug,
          );
          return currentCourse === entitlementCourse;
        })
        .map((entitlement: UserEntitlement) => {
          const remainingEntitlementTime = getRemainingEntitlementTime(
            entitlement.entitlement_info.end_time,
          );

          const remainingLabUsage =
            entitlement.quotas?.lab_hours?.allowed > 0 &&
            getLabUsageRemaining(
              Math.round(entitlement.quotas.lab_hours.used),
              entitlement.quotas.lab_hours.allowed,
            );

          return isMobile ? (
            <ExpandableSubscriptionWidget
              entitlement={entitlement}
              remainingEntitlementTime={remainingEntitlementTime}
              remainingLabUsage={remainingLabUsage}
              key={entitlement.entitlement_info.offering_slug}
            />
          ) : (
            <ul
              className="subscription-info"
              key={entitlement.entitlement_info.offering_slug}
            >
              <li>
                <strong>{entitlement.entitlement_info.title}:</strong>
              </li>
              <li>
                {entitlement.entitlement_info.modality === 'alacarte' &&
                remainingEntitlementTime > 0 &&
                remainingEntitlementTime <= 1 ? (
                  <strong>{t('Last day')}</strong>
                ) : (
                  <>
                    {t('Days remaining')}&nbsp;
                    <strong>{Math.round(remainingEntitlementTime)}</strong>
                  </>
                )}
              </li>
              {entitlement.quotas?.lab_hours?.allowed > 0 && (
                <>
                  <span>|</span>
                  <li>{remainingLabUsage}</li>
                </>
              )}
              {entitlement.quotas?.exams?.allowed > 0 && (
                <>
                  <span>|</span>
                  <li>
                    {t('Exams')}&nbsp;
                    <strong>
                      {entitlement.quotas.exams.used || 0}/
                      {entitlement.quotas.exams.allowed}
                    </strong>
                  </li>
                </>
              )}
            </ul>
          );
        })}
    </div>
  );
};

export default withErrorBoundary(
  observer(SubscriptionWidget),
  ComponentError,
  handleError,
);
